import { type ReactNode, useCallback, useRef } from 'react';

import { useRequestAnimationFrame } from 'pkg/hooks/animation';
import { useEventListener } from 'pkg/hooks/events';

import { useCursorAttraction } from 'atoms/cursor/hooks/attraction';
import { useCursorCollision } from 'atoms/cursor/hooks/collision';

import css from './styles.module.css';

export default function Cursor(): ReactNode {
	const cursor = useRef<HTMLDivElement>(null);
	const pointer = useRef<Coord>({ x: -200, y: -200 });

	const attraction = useCursorAttraction();
	const collision = useCursorCollision();

	const prevMagnetic = useRef<Nullable<HTMLElement>>(null);
	const prevHoverable = useRef<Nullable<HTMLElement>>(null);

	useRequestAnimationFrame(() => {
		attraction.tick();

		const target = cursor.current;
		const magnetic = attraction.activeNode.current;
		const hoverable = collision.hoveredRef.current;

		if (prevMagnetic.current && prevMagnetic.current !== magnetic) {
			prevMagnetic.current.removeAttribute('data-cursor-magnetic-focus');
		}

		if (magnetic) {
			magnetic.setAttribute('data-cursor-magnetic-focus', '');
		}

		prevMagnetic.current = magnetic;

		if (prevHoverable.current && prevHoverable.current !== hoverable) {
			prevHoverable.current.removeAttribute('data-cursor-hoverable-focus');
		}

		if (hoverable) {
			hoverable.setAttribute('data-cursor-hoverable-focus', '');
		}

		prevHoverable.current = hoverable;

		if (target) {
			target.classList.toggle(css.attracting, !!magnetic);
			target.classList.toggle(css.hovering, !!hoverable && !magnetic);

			if (magnetic) {
				const rect = magnetic.getBoundingClientRect();
				const style = getComputedStyle(magnetic);

				target.style.setProperty(
					'--cursor-x',
					`${rect.left + rect.width / 2}px`
				);
				target.style.setProperty(
					'--cursor-y',
					`${rect.top + rect.height / 2}px`
				);
				target.style.setProperty('--cursor-radius', style.borderRadius);

				target.style.width = `${rect.width}px`;
				target.style.height = `${rect.height}px`;
			} else {
				target.style.setProperty('--cursor-x', `${pointer.current.x}px`);
				target.style.setProperty('--cursor-y', `${pointer.current.y}px`);
				target.style.removeProperty('--cursor-radius');

				target.style.width = '';
				target.style.height = '';
			}
		}
	});

	const onMove = useCallback(
		(e: MouseEvent): void => {
			pointer.current = { x: e.clientX, y: e.clientY };

			attraction.update(e.clientX, e.clientY);
			collision.update(e.clientX, e.clientY);
		},
		[attraction, collision]
	);

	const onClick = useCallback(
		(e: MouseEvent): void => {
			const magnetic = attraction.activeNode.current;

			if (magnetic && !magnetic.contains(e.target as Node)) {
				e.preventDefault();
				e.stopPropagation();
				magnetic.click();
			}
		},
		[attraction]
	);

	useEventListener('mousemove', onMove, [onMove]);
	useEventListener('click', onClick, [onClick]);

	return <div ref={cursor} className={css.cursor} />;
}
